import classnames from 'classnames';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Header } from '@jsluna/header';
import { Footer } from '@jsluna/footer';
import { filterProps, debounce, getComponentName } from '@jsluna/utils';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var DEBOUNCE_WAIT = 100;

var Page = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Page, _Component);

  function Page(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      isSticky: false
    };
    _this.handleScroll = _this.handleScroll.bind(_assertThisInitialized(_this));
    _this.debouncedHandleScroll = debounce(_this.handleScroll, DEBOUNCE_WAIT);
    return _this;
  }

  var _proto = Page.prototype;

  _proto.componentDidMount = function componentDidMount() {
    var stickyHeader = this.props.stickyHeader;

    if (stickyHeader) {
      window.addEventListener('scroll', this.debouncedHandleScroll);
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    var stickyHeader = this.props.stickyHeader;

    if (stickyHeader) {
      window.removeEventListener('scroll', this.debouncedHandleScroll);
    }
  };

  _proto.handleScroll = function handleScroll() {
    var currentlySticky = this.state.isSticky;
    var _this$props = this.props,
        stickyThreshold = _this$props.stickyThreshold,
        stickyOffset = _this$props.stickyOffset,
        onHeaderSticky = _this$props.onHeaderSticky;
    var pageY = window.scrollY || window.pageYOffset;
    var isSticky;

    if (pageY > stickyThreshold && !currentlySticky) {
      isSticky = true;
    } else if (pageY <= stickyThreshold - stickyOffset) {
      isSticky = false;
    }

    if (typeof isSticky !== 'undefined') {
      this.setState({
        isSticky: isSticky
      });

      if (onHeaderSticky) {
        onHeaderSticky(isSticky);
      }
    }
  };

  _proto.render = function render() {
    var isSticky = this.state.isSticky;

    var _this$props2 = this.props,
        element = _this$props2.element,
        children = _this$props2.children,
        className = _this$props2.className,
        stickyHeader = _this$props2.stickyHeader,
        customHeader = _this$props2.customHeader,
        rest = _objectWithoutPropertiesLoose(_this$props2, ["element", "children", "className", "stickyHeader", "customHeader"]);

    var Element = element;
    return /*#__PURE__*/React.createElement(Element, _extends({}, filterProps(rest, ['stickyThreshold', 'stickyOffset', 'onHeaderSticky']), {
      className: classnames('ln-o-page', customHeader && 'ln-o-page--custom-header', stickyHeader && 'ln-o-page--sticky-header', stickyHeader && isSticky && 'ln-o-page--is-sticky', className)
    }), children);
  };

  return Page;
}(Component);

Page.propTypes = {
  /** Allows the top-level element to be customized */
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,
  customHeader: PropTypes.bool,

  /** If the layout should fix the header to the top of the window */
  stickyHeader: PropTypes.bool,

  /** When sticky modifier class should apply, defaults to initial value of $ln-header-height + $ln-space-px */
  stickyThreshold: PropTypes.number,

  /**
   * Modifier to change when sticky class should be removed to avoid rubber band effect,
   * set to height of element being removed on sticky. Defaults to stickyThreshold / 2.
   */
  stickyOffset: PropTypes.number,

  /** Event callback when header changes from fixed to static */
  onHeaderSticky: PropTypes.func
};
Page.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  customHeader: false,
  stickyHeader: false,
  stickyThreshold: 72,
  stickyOffset: 36,
  onHeaderSticky: undefined
};
Page.displayName = 'Page';

var PageBody = function PageBody(_ref) {
  var element = _ref.element,
      children = _ref.children;
  var Element = element;
  return /*#__PURE__*/React.createElement(Element, {
    className: "ln-o-page__body"
  }, children);
};

PageBody.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node
};
PageBody.defaultProps = {
  element: 'div',
  children: undefined
};
PageBody.displayName = 'PageBody';

var PageFooter = function PageFooter(_ref) {
  var element = _ref.element,
      children = _ref.children;
  var Element = element;
  return /*#__PURE__*/React.createElement(Element, {
    className: "ln-o-page__footer"
  }, children);
};

PageFooter.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node
};
PageFooter.defaultProps = {
  element: 'div',
  children: undefined
};
PageFooter.displayName = 'PageFooter';

var PageHeader = function PageHeader(_ref) {
  var element = _ref.element,
      children = _ref.children;
  var Element = element;
  return /*#__PURE__*/React.createElement(Element, {
    className: "ln-o-page__header"
  }, children);
};

PageHeader.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node
};
PageHeader.defaultProps = {
  element: 'div',
  children: undefined
};
PageHeader.displayName = 'PageHeader';

var SiteLayout = /*#__PURE__*/function (_Component) {
  _inheritsLoose(SiteLayout, _Component);

  function SiteLayout(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      headerIsStuck: false
    };
    _this.handleHeaderSticky = _this.handleHeaderSticky.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = SiteLayout.prototype;

  _proto.handleHeaderSticky = function handleHeaderSticky(isSticky) {
    this.setState({
      headerIsStuck: isSticky
    });
  };

  _proto.render = function render() {
    var headerIsStuck = this.state.headerIsStuck;

    var _this$props = this.props,
        children = _this$props.children,
        className = _this$props.className,
        customHeader = _this$props.customHeader,
        bodyFlushTop = _this$props.bodyFlushTop,
        stickyHeader = _this$props.stickyHeader,
        rest = _objectWithoutPropertiesLoose(_this$props, ["children", "className", "customHeader", "bodyFlushTop", "stickyHeader"]);

    var body = [];
    var header;
    var footer;
    React.Children.forEach(children, function (child) {
      if (React.isValidElement(child)) {
        var type = child.type;
        var componentName = getComponentName(child);

        if (type === Header || componentName === getComponentName(Header)) {
          header = child;
        } else if (type === Footer || componentName === getComponentName(Footer)) {
          footer = child;
        } else {
          body.push(child);
        }
      }
    });
    return /*#__PURE__*/React.createElement(Page, _extends({
      className: className,
      stickyHeader: stickyHeader,
      customHeader: customHeader,
      onHeaderSticky: this.handleHeaderSticky
    }, rest), header && /*#__PURE__*/React.createElement(PageHeader, null, React.cloneElement(header, {
      isSticky: stickyHeader,
      isStuck: headerIsStuck
    })), /*#__PURE__*/React.createElement(PageBody, null, body), footer && /*#__PURE__*/React.createElement(PageFooter, null, footer));
  };

  return SiteLayout;
}(Component);

SiteLayout.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /** Override to block the padding offset normally appied to HeaderBody */
  bodyFlushTop: PropTypes.bool,

  /** Flag to set whether the header being used isn't the Luna default  */
  customHeader: PropTypes.bool,

  /** Applies the fixed header behaviour from Page */
  stickyHeader: PropTypes.bool
};
SiteLayout.defaultProps = {
  className: undefined,
  bodyFlushTop: false,
  customHeader: false,
  stickyHeader: false
};
SiteLayout.displayName = 'SiteLayout';

export { Page, PageBody, PageFooter, PageHeader, SiteLayout };
//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/@jsluna/site-layout/dist/index.js
// module id = 236
// module chunks = 0